import Links from "./components/Links";
import routes from "routes";
import logo from "assets/img/sahl_black2.png";
import launcherIcon from "assets/img/1709819597150_ic_launcher.png";
import "./style.scss";
import { MdLogout } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "components/ui/tooltip";
import { useDispatch } from "react-redux";
import { authActions } from "views/auth/sagas/authSlice";
import {
  LuUser,
  LuUsers,
  LuChevronDown,
  LuChevronsLeft,
  LuChevronsRight,
} from "react-icons/lu";
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

interface ProfileData {
  isSuperAdmin?: boolean;
  user?: { userName?: string };
  userName?: string;
  role?: { roleName?: string };
  roleName?: string;
}

const ProfileButton = ({ open }: { open: boolean }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const profileString = sessionStorage.getItem("meProfile");
  const profile: ProfileData | null = profileString
    ? JSON.parse(profileString)
    : null;
  const isSuperAdmin = profile?.isSuperAdmin === true;
  const displayName = profile?.user?.userName || profile?.userName || "Admin";
  const displayRole = isSuperAdmin
    ? "Super Admin"
    : profile?.role?.roleName || profile?.roleName || "Admin";

  const togglePopup = useCallback(() => {
    setShowPopup((prev) => !prev);
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        !popupRef.current?.contains(e.target as Node)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSignOut = useCallback(() => {
    dispatch(
      authActions.logout({
        phone: sessionStorage.getItem("phone"),
        deviceToken: sessionStorage.getItem("token"),
      })
    );
    sessionStorage.removeItem("token");
    navigate("/");
  }, [dispatch, navigate]);

  const handleAdminUserManagement = useCallback(() => {
    setShowPopup(false);
    navigate("/superuser/admin-user-management");
  }, [navigate]);

  // Collapsed (icon-only) mode
  if (!open) {
    return (
      <div className="sidebar-profile-wrapper" ref={ref}>
        <Tooltip
          content={`${displayName} (${displayRole})`}
          positioning={{ placement: "right", gutter: 12 }}
          showArrow
        >
          <button
            type="button"
            onClick={togglePopup}
            className="sidebar-collapsed-profile-trigger"
            aria-label="User Profile"
          >
            <div className="sidebar-profile-avatar">
              <LuUser />
            </div>
          </button>
        </Tooltip>

        {showPopup &&
          createPortal(
            <div ref={popupRef} className="sidebar-collapsed-profile-popup">
              <div className="sidebar-collapsed-profile-header">
                <p className="sidebar-collapsed-profile-name">{displayName}</p>
                <p className="sidebar-collapsed-profile-role">{displayRole}</p>
              </div>
              <div className="sidebar-collapsed-profile-actions">
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={handleAdminUserManagement}
                    className="sidebar-collapsed-profile-button"
                  >
                    <LuUsers />
                    <span>Admin User Management</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="sidebar-collapsed-profile-button sign-out"
                >
                  <MdLogout />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>,
            document.body
          )}
      </div>
    );
  }

  // Expanded mode
  return (
    <div className="sidebar-profile-wrapper" ref={ref}>
      <button
        type="button"
        onClick={togglePopup}
        className={`sidebar-profile-button ${showPopup ? "is-open" : ""}`}
        aria-label="User Profile Menu"
      >
        <div className="sidebar-profile-avatar">
          <LuUser />
        </div>
        <div className="sidebar-profile-info">
          <p className="sidebar-profile-name">{displayName}</p>
          <p className="sidebar-profile-role">{displayRole}</p>
        </div>
        <LuChevronDown
          className={`sidebar-profile-chevron ${showPopup ? "is-rotated" : ""}`}
        />
      </button>

      {/* Popup menu */}
      {showPopup && (
        <div className="sidebar-profile-popup">
          <div className="sidebar-profile-popup-header">
            <p className="sidebar-profile-popup-name">{displayName}</p>
            <p className="sidebar-profile-popup-role">{displayRole}</p>
          </div>
          <div className="sidebar-profile-popup-actions">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={handleAdminUserManagement}
                className="sidebar-profile-popup-button"
              >
                <LuUsers />
                <span>Admin User Management</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="sidebar-profile-popup-button sign-out"
            >
              <MdLogout />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Sidebar = (props: {
  open: boolean;
  handleClick: () => void;
}) => {
  const { open, handleClick } = props;

  return (
    <aside className={`sidebar-container ${open ? "is-open" : "is-closed"}`}>
      <div className={`sidebar-header ${open ? "is-open" : "is-closed"}`}>
        {open ? (
          <>
            <div className="sidebar-logo-block">
              <img src={logo} alt="Sahl" className="sidebar-logo-image" />
              <span className="sidebar-logo-subtitle">ADMIN</span>
            </div>
            <button
              type="button"
              className="sidebar-toggle-button is-open"
              onClick={handleClick}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <LuChevronsLeft className="sidebar-toggle-icon" />
            </button>
          </>
        ) : (
          <div className="sidebar-collapsed-header">
            <button
              type="button"
              className="sidebar-collapsed-logo-button"
              onClick={handleClick}
              title="Expand sidebar"
              aria-label="Expand sidebar"
            >
              <img
                src={launcherIcon}
                alt="Expand sidebar"
                className="sidebar-collapsed-s-logo"
              />
            </button>
          </div>
        )}
      </div>

      <div className="sidebar-divider" />

      {/* Nav items */}
      <nav className={`sidebar-main ${open ? "is-open" : "is-closed"}`}>
        <Links routes={routes} openSide={open} />
      </nav>

      {/* Profile Button */}
      <div className="sidebar-profile-section">
        <ProfileButton open={open} />
      </div>
    </aside>
  );
};

export default Sidebar;
